import { FormattedMessage } from 'react-intl';
import cx from 'clsx';
import { type ReactNode, Suspense } from 'react';
import { toDataURL } from 'qrcode';
import { IconEmptystateUpgrade } from '../../../../assets/icons/icon-emptystate-upgrade.tsx';
import { usePdfPreviewContext } from '../../pdf-preview-context.tsx';
import styles from './payment-link.module.css';

interface PaymentLinkProps {
  paymentLink?: string | null;
  className?: string;
}

export function PaymentLink({ paymentLink, className }: PaymentLinkProps): ReactNode {
  const { displayLanguage } = usePdfPreviewContext();

  if (paymentLink) {
    return (
      <div className={cx(styles.wrapper, className)} data-testid="pdf-preview-payment-link">
        <figure className={styles.qrCodeContainer}>
          <Suspense fallback={<div className={styles.qrCode} />}>
            <QrCode paymentLink={paymentLink} />
          </Suspense>
          <figcaption className="caption" data-testid="pdf-preview-payment-link-title">
            <FormattedMessage
              id="pdf-preview.payment-footer.text"
              values={{ language: displayLanguage }}
            />
          </figcaption>
        </figure>
        <a
          className={styles.paymentCta}
          data-testid="pdf-preview-payment-link-cta"
          href={paymentLink}
          rel="noopener noreferrer"
          target="_blank"
        >
          <IconEmptystateUpgrade aria-hidden="true" />
          <FormattedMessage
            id="pdf-preview.payment-footer.cta"
            values={{ language: displayLanguage }}
          />
        </a>
      </div>
    );
  }

  return null;
}

const qrCodeCache = new Map<string, string>();

function useSuspenseQrCode(src: string): string {
  const qrCodeDataUrl = qrCodeCache.get(src);

  if (!qrCodeDataUrl) {
    // eslint-disable-next-line @typescript-eslint/no-throw-literal -- we throw a Promise to trigger Suspense
    throw new Promise(resolve => {
      toDataURL(src)
        .then(dataURL => {
          qrCodeCache.set(src, dataURL);
          resolve(null);
        })
        .catch(() => {
          resolve(null);
        });
    });
  }

  return qrCodeDataUrl;
}

interface QrCodeProps {
  paymentLink: string;
}

function QrCode({ paymentLink }: QrCodeProps): ReactNode {
  const qrCodeDataUrl = useSuspenseQrCode(paymentLink);

  return (
    <img
      alt=""
      className={styles.qrCode}
      data-testid="pdf-preview-payment-link-qr-code"
      src={qrCodeDataUrl}
    />
  );
}
